import _ from 'lodash'
import utils from '@eitje/utils'
import {deepTransformValues, sanitizeMoment} from '../backend'
import {config} from '../config'
import {buildScopePruningPlan} from '../reducers/prune_scopes'

export const getIndexScope = ({name, filters, inverted, refresh, prunable}) => {
	const scope = buildScope({kind: name, filters, prunable})
	if (refresh) return {scope}
	return {scope, ...getIndexScopeState({name, inverted, scope})}
}

export const getIndexScopeId = ({name, filters}) => buildScope({kind: name, filters}).id

const buildScope = ({kind, filters = {}, prunable = false}) => {
	const normalizedFilters = normalizeScopeValues(filters)
	return {id: buildScopeId({kind, filters: normalizedFilters}), kind, filters: normalizedFilters, prunable}
}

const normalizeScopeValues = values => deepTransformValues(values, sanitizeMoment)

const buildScopeId = ({kind, filters}) => {
	if (!utils.exists(filters)) return kind
	const sortedStringified = JSON.stringify(filters, Object.keys(filters).sort())
	return `${kind}-${sortedStringified}`
}

const getIndexScopeState = ({name, inverted, scope}) => {
	const customStampField = config.stampFields[name]
	const stampParams = {}
	const state = config.store.getState()
	const allItems = state.records[name]
	if (!allItems) return {}
	const scopes = state.records.scopes
	const {ownedScopeIds, relatedScopeIds} = getScopeRelations({scope, scopes})
	const currentItems = allItems.filter(i => utils.intersects(i.scopeIds, ownedScopeIds))
	const relatedItems = allItems
		.filter(
			i => !i.skipFetchStamp && i.updated_at && utils.intersects(i.scopeIds, relatedScopeIds) && recordOverlapsScopeDateRange(i, scope),
		)
		.map(i => ({id: i.id, updated_at: i.updated_at}))

	if (customStampField) {
		const stampItems = currentItems.filter(i => !i.skipFetchStamp)
		stampParams.lastUpdatedStamp = findStamp(stampItems, 'updated_at', inverted)
		stampParams.lastCreatedStamp = findStamp(stampItems, customStampField, inverted)
	} else {
		stampParams.lastUpdatedStamp = scopes[scope.id]?.lastUpdatedStamp
	}

	return {
		stampParams,
		currentIds: currentItems._map('id'),
		relatedItems,
		unscopedItems: allItems.filter(i => !utils.exists(i.scopeIds)).map(i => ({id: i.id, updated_at: i.updated_at})),
	}
}

const getScopeRelations = ({scope, scopes = {}}) => {
	const ownedScopeIds = []
	const relatedScopeIds = []

	Object.values(scopes).forEach(existingScope => {
		if (scopeIncludesScope(scope, existingScope)) ownedScopeIds.push(existingScope.id)
		else if (
			utils.exists(existingScope.filters) &&
			(scopeIncludesScope(existingScope, scope) || scopesHaveOverlappingDateRanges(scope, existingScope))
		) {
			relatedScopeIds.push(existingScope.id)
		}
	})

	return {ownedScopeIds, relatedScopeIds}
}

const scopeIncludesScope = (broadScope, narrowScope) => {
	if (broadScope.kind !== narrowScope.kind) return false
	return Object.entries(broadScope.filters).every(([key, value]) => {
		if (key === 'start_date') return value <= narrowScope.filters.start_date
		if (key === 'end_date') return value >= narrowScope.filters.end_date
		return _.isEqual(narrowScope.filters[key], value)
	})
}

const scopesHaveOverlappingDateRanges = (scope, existingScope) => {
	if (scope.kind !== existingScope.kind) return false

	const {start_date: start, end_date: end} = scope.filters
	const {start_date: existingStart, end_date: existingEnd} = existingScope.filters
	if (!start || !end || !existingStart || !existingEnd) return false
	if (start > existingEnd || existingStart > end) return false

	const scopeFilters = _.omit(scope.filters, ['start_date', 'end_date'])
	const existingFilters = _.omit(existingScope.filters, ['start_date', 'end_date'])
	const sharedKeys = _.intersection(Object.keys(scopeFilters), Object.keys(existingFilters))

	return sharedKeys.every(key => _.isEqual(scopeFilters[key], existingFilters[key]))
}

const recordOverlapsScopeDateRange = (record, scope) => {
	const {start_date: start, end_date: end} = scope.filters
	if (!start || !end || !record.date) return true

	return start <= record.date && record.date <= end
}

export const buildScopeRefetchPlan = ({kind, recordIds}) => {
	if (recordIds.length === 0) return []

	const state = config.store.getState()
	const records = state.records[kind]
	const scopes = state.records.scopes
	const affectedRecords = records.filter(record => recordIds.includes(record.id))
	const scopesToRefetch = []

	affectedRecords.forEach(record => {
		record.scopeIds.forEach(scopeId => {
			const scope = scopes[scopeId]
			if (!scope) return
			if (scopesToRefetch.some(plannedScope => plannedScope.id === scopeId)) return
			scopesToRefetch.push(scope)
		})
	})

	return scopesToRefetch
}

export const pruneScopes = scopes => {
	const state = config.store.getState()
	const pruningPlan = buildScopePruningPlan(state.records.scopes, scopes)
	if (!utils.exists(pruningPlan)) return
	config.store.dispatch({type: 'PRUNE_SCOPES', pruningPlan})
}

function findStamp(items, field, inverted = false) {
	const func = inverted ? 'getMin' : 'getMax'
	const item = utils[func](items, field)
	return item ? item[field] : null
}
